import React, { useState, useEffect, useContext, useRef } from 'react';
import Select from 'react-select';
import i18next from '../../i18n.js';
import { loadListData, loadAvatar, loadSubordinates } from '../helpers/HelperFunctions.js';
import { listTypes, resources, UserContext } from '../../constants.js';

const UserInput = (props) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [opened, setOpened] = useState(false);
    const [userImage, setUserImage] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const lastRequestId = useRef(0);
    const lastTimer = useRef(null);
    const isFirstSearch = useRef(true);

    const userContext = useContext(UserContext);

    useEffect(() => {
        if (props.value && users.length === 0) {
            loadData();
        }
    }, [props.value, props.valueFieldName]);

    useEffect(() => {
        if (!props.disabled) {
            loadData();
        }
    }, [props.disabled]);

    useEffect(() => {
        if (opened && users.length === 0) {
            loadData();
        }
    }, [opened])

    useEffect(() => {
        loadData();
    }, [props.subordinatesOnly]);

    useEffect(() => {
        if (isFirstSearch.current) {
            isFirstSearch.current = false;
            return;
        }
        if (props.subordinatesOnly) {
            return;
        }
        if (lastTimer.current) {
            clearTimeout(lastTimer.current);
        }
        const timer = setTimeout(() => loadData(searchQuery, true), 400);
        lastTimer.current = timer;
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        const selected = users.find(user => user.fields.find(f => f.name === (props.valueFieldName ?? 'NodeRef')).value === props.value);
        if (selected) {
            if (selected.fields.find(f => f.name === 'AvatarNodeRef')?.value) {
                loadAvatar(selected.fields.find(f => f.name === 'UserName').value, selected.fields.find(f => f.name === 'AvatarNodeRef').value.split('/').pop()).then(url => {
                    setUserImage(url ?? resources.defaulUserImg);
                });
            }
            else {
                setUserImage(resources.defaulUserImg);
            }
        }
    }, [users])

    const loadData = (query = searchQuery, force = false) => {
        if (!loading || force) {
            setLoading(true);
            if (props.subordinatesOnly) {
                loadSubordinates().then(subordinates => {
                    let userList = [];
                    if (subordinates?.length > 0) {
                        userList = subordinates.map(item => {
                            return {
                                id: item.nodeRef.split('/').pop(),
                                fields: [
                                    {
                                        name: 'Id',
                                        value: item.nodeRef.split('/').pop()
                                    },
                                    {
                                        name: 'FullName',
                                        value: item.fullName
                                    },
                                    {
                                        name: 'UserName',
                                        value: item.userName
                                    },
                                    {
                                        name: 'NodeRef',
                                        value: item.nodeRef
                                    }
                                ]
                            }
                        });
                    }
                    if (props.allowSelfSelect && userContext) {
                        userList.unshift({
                            id: userContext.nodeRef.split('/').pop(),
                            fields: [
                                {
                                    name: 'Id',
                                    value: userContext.nodeRef.split('/').pop()
                                },
                                {
                                    name: 'FullName',
                                    value: userContext.userName
                                },
                                {
                                    name: 'UserName',
                                    value: userContext.login
                                },
                                {
                                    name: 'NodeRef',
                                    value: userContext.nodeRef
                                },
                                {
                                    name: 'AvatarNodeRef',
                                    value: userContext.avatarId
                                }
                            ]
                        });
                    }
                    setUsers(userList);
                });
                setLoading(false);
            }
            else {
                let filter = null;
                if (props.disabled === 'disabled' && props.value) {
                    const fieldName = props.valueFieldName ?? 'NodeRef';
                    switch (fieldName) {
                        case 'NodeRef':
                            filter = [{
                                name: 'Id',
                                values: [props.value.split('/').pop()]
                            }];
                            break;
                        case 'UserName':
                            filter = [{
                                name: 'UserName',
                                values: [props.value]
                            }];
                            break;
                    }
                }
                const requestId = ++lastRequestId.current;
                loadListData(listTypes.Users, filter, 0, 200, '', false, query).then(data => {
                    // Ігноруємо відповіді на застарілі пошукові запити
                    if (requestId !== lastRequestId.current) {
                        return;
                    }
                    if (data && data.items && (data.items.length > 0 || query)) {
                        const valueFieldName = props.valueFieldName ?? 'NodeRef';
                        const loaded = data.items.filter(item => item.fields.find(f => f.name === 'UserName').value !== userContext?.login || props.allowSelfSelect);
                        setUsers(prevUsers => {
                            // Зберегаємо обраного користувача, якщо він не завантажений у поточний список
                            const selected = props.value && prevUsers.find(user => user.fields.find(f => f.name === valueFieldName)?.value === props.value);
                            if (selected && !loaded.some(user => user.fields.find(f => f.name === valueFieldName)?.value === props.value)) {
                                return [selected, ...loaded];
                            }
                            return loaded;
                        });
                    }
                    setLoading(false);
                });
            }
        }
    }

    const onChange = (selectedItem) => {
        if (props.onChange) {
            props.onChange({
                target: {
                    value: selectedItem?.value ?? '',
                    name: props.name
                }
            })
        }
        if (selectedItem) {
            const selected = users.find(user => user.fields.find(f => f.name === (props.valueFieldName ?? 'NodeRef')).value === selectedItem.value);
            if (selected) {
                if (selected.fields.find(f => f.name === 'AvatarNodeRef')?.value) {
                    loadAvatar(selected.fields.find(f => f.name === 'UserName').value, selected.fields.find(f => f.name === 'AvatarNodeRef').value.split('/').pop()).then(url => {
                        setUserImage(url ?? resources.defaulUserImg);
                    });
                }
                else {
                    setUserImage(resources.defaulUserImg);
                }
            }
        }
        else {
            setUserImage(null);
        }
    }

    const onInputChange = (inputValue, actionMeta) => {
        if (props.subordinatesOnly) {
            return;
        }
        if (actionMeta.action === 'input-change') {
            setSearchQuery(inputValue.trim());
        }
        else if (actionMeta.action === 'set-value' || actionMeta.action === 'menu-close' || actionMeta.action === 'input-blur') {
            setSearchQuery('');
        }
    }

    const options = users.map(item => {
        return {
            label: item.fields.find(f => f.name === 'FullName').value,
            value: item.fields.find(f => f.name === (props.valueFieldName ?? 'NodeRef')).value
        }
    });

    return (<div className='user-input-control' name={props.name} >
        {userImage && <img src={userImage} />}
        <Select
            name={props.name + '_input'}
            options={options}
            isLoading={(opened && users.length === 0) || (!props.subordinatesOnly && loading)}
            onMenuOpen={() => setOpened(true)}
            placeholder={props.disabled ? '' : (props.placeholder ?? i18next.t('userInput.search'))}
            noOptionsMessage={() => i18next.t('userInput.notFound')}
            loadingMessage={() => i18next.t('userInput.loading')}
            isClearable={true}
            classNamePrefix={'user_input'}
            onChange={onChange}
            onInputChange={onInputChange}
            value={options.find(opt => opt.value === props.value)}
            isDisabled={props.disabled}
            menuPortalTarget={document.body}
            menuPosition='fixed'
            menuPlacement='auto'
        />
    </div>);
}
export default UserInput;